import {
  createContext,
  useContext,
  useState,
  useEffect,
  type PropsWithChildren,
} from "react";

import useStore from "./store";
import { BOI_THEME_TOKENS } from "../constants";
import type { ThemeTokens } from "../types";

type TDesignTokensContext = {
  tokens: ThemeTokens;
  theme: string;
  updateTheme: (theme: string) => void;
};

const DesignTokensContext = createContext<TDesignTokensContext>(
  {} as TDesignTokensContext
);

type SystemTheme = "light" | "dark";

const lightSchemeMatcher = window.matchMedia("(prefers-color-scheme: light)");

type DesignTokensProviderProps = PropsWithChildren & {
  theme?: string;
  themeTokens?: {
    light: ThemeTokens;
    [key: string]: ThemeTokens;
  };
};

export function DesignTokensProvider(props: DesignTokensProviderProps) {
  const {
    theme: externalTheme,
    themeTokens = BOI_THEME_TOKENS,
    children,
  } = props;

  const [systemTheme, setSystemTheme] = useState<SystemTheme>(() => {
    return lightSchemeMatcher.matches ? "light" : "dark";
  });

  const localStorageTheme = useStore((store) => store.theme);
  const updateTheme = useStore((store) => store.updateTheme);

  useEffect(() => {
    function handleChange(event: MediaQueryListEvent) {
      setSystemTheme(event.matches ? "light" : "dark");
    }

    lightSchemeMatcher.addEventListener("change", handleChange);

    return () => lightSchemeMatcher.removeEventListener("change", handleChange);
  }, []);

  const theme = externalTheme || localStorageTheme || systemTheme;
  const tokens = (themeTokens as any)[theme] || themeTokens.light;

  return (
    <DesignTokensContext.Provider value={{ tokens, theme, updateTheme }}>
      {children}
    </DesignTokensContext.Provider>
  );
}

export function useDesignTokens() {
  return useContext(DesignTokensContext);
}

export default DesignTokensContext;
